
import React, { useRef, useEffect } from "react";
import { useDeepDive } from "@/hooks/useDeepDive";
import DeepDiveForm from "@/components/DeepDiveForm";
import CommentaryResult from "@/components/CommentaryResult";
import LoadingScriptures from "@/components/LoadingScriptures";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { useScrollToTop } from "@/hooks/useScrollToTop";

const DeepDive = () => {
  useScrollToTop();

  const {
    scriptureReference,
    setScriptureReference,
    isLoading,
    isSaving,
    commentary,
    handleSubmit,
    saveCommentary,
  } = useDeepDive();
  
  const resultRef = useRef<HTMLDivElement>(null);
  
  // Scroll to results when they load
  useEffect(() => {
    if (commentary && !isLoading && resultRef.current) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [commentary, isLoading]);

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="pt-24 pb-16 text-center">
        <h1 className="text-5xl md:text-7xl font-playfair mb-6">
          <span className="text-black">Deep Dive </span>
          <span className="text-heaven-gold">Commentary</span>
        </h1>
        <p className="text-gray-600 max-w-3xl mx-auto px-4 text-lg leading-relaxed">
          Explore Scripture through rigorous hermeneutical analysis that illuminates the 
          theological depth, historical context, and scholarly interpretation of any verse 
          or biblical passage.
        </p>
      </div>

      {/* Search Form Container */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-md p-8">
          <h2 className="text-3xl text-[#4169E1] text-center mb-3">
            What Passage Would You Like to Explore?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Enter a Bible verse, passage, or chapter to receive an in-depth theological analysis.
          </p>
          
          <DeepDiveForm
            reference={scriptureReference}
            isLoading={isLoading}
            onReferenceChange={setScriptureReference}
            onSubmit={handleSubmit}
          />
        </div>

        {/* Results Section */}
        {isLoading ? (
          <LoadingScriptures />
        ) : (
          commentary && (
            <div ref={resultRef} className="mt-8">
              <div className="mb-8">
                <h2 className="text-2xl font-playfair font-bold text-gray-800 mb-2">
                  Commentary on "{scriptureReference}"
                </h2>
                <p className="text-gray-600">
                  Here's your in-depth theological analysis.
                </p>
              </div>
              
              <CommentaryResult commentary={commentary} />
              <div className="flex justify-end mt-6">
                <Button
                  onClick={saveCommentary}
                  disabled={isSaving}
                  className="bg-heaven-gold hover:bg-heaven-gold/80 text-white"
                >
                  {isSaving ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save to Dashboard
                    </>
                  )}
                </Button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default DeepDive;
